import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Box, Button, CircularProgress } from "@mui/material";
import NewShirtForm from "../../components/ShirtForm/NewShirtForm";
import { NewShirt, Image, Property } from "../../services/shirts/types";
import ShirtImages from "../../components/ShirtImages/ShirtImages";
import BackdropLoaderService from "../../components/BackdropLoader/backdropLoaderService";
import DialogService from "../../components/Dialog/dialogService";
import ShirtsService from "../../services/shirts/shirtsService";
import NotificationService from "../../components/Notification/notificationService";
import { shirtDefault } from "../../services/shirts/types";
import { Dictionary } from "../../types/types";

const NewShirtView = () => {
  const [shirtData, setShirtData] = useState<NewShirt>(shirtDefault);
  const [images, setImages] = useState<Array<Image>>([]);
  const [wornProperty, setWornProperty] = useState<Property | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState<Dictionary<boolean>>({});

  const { close: closeBDL } = BackdropLoaderService();
  const { closeReload: closeDialog } = DialogService();
  const { success, error } = NotificationService();
  const { newShirt, updateProperty, updateImages } = ShirtsService();

  const mandatoryFields = [
    "title",
    "purchase_price",
    "certificate",
    "private",
    "team_id",
    "competition_id",
    "variant_id",
    "season_id",
    "brand_id",
    "shirt_type_id",
    "condition_id",
  ];

  const checkMandatoryFields = (
    fields: Array<string>,
    shirt: NewShirt,
    images: Array<Image>
  ) => {
    let result = true;
    let newErrors: Dictionary<boolean> = {};

    fields.forEach((f) => {
      // Check if field has value
      const fieldValue = shirt[f as keyof NewShirt];
      const hasValue = fieldValue !== undefined && fieldValue !== "";

      // For fields that can have custom alternatives, check custom field too
      let hasCustomValue = false;
      if (f === "team_id") {
        hasCustomValue =
          shirt.team_custom !== undefined && shirt.team_custom !== "";
      } else if (f === "brand_id") {
        hasCustomValue =
          shirt.brand_custom !== undefined && shirt.brand_custom !== "";
      } else if (f === "player_id") {
        hasCustomValue =
          shirt.player_custom !== undefined && shirt.player_custom !== "";
      }

      // Field is valid if it has a value OR has a custom value
      const isFieldValid = hasValue || hasCustomValue;

      if (!isFieldValid) {
        result = false;
        newErrors[f as keyof NewShirt] = true;
      }
    });

    if (images.length === 0) {
      result = false;
      newErrors["images"] = true;
    }

    setErrors(newErrors);
    return result;
  };

  const updateField = (field: string, value: any) => {
    let copyData: NewShirt = { ...shirtData };
    copyData[field as keyof typeof copyData] = value;
    setShirtData(copyData);
  };

  const removeImage = (index: number) => {
    let arr = [...images];
    if (index >= 0 && index < arr.length) {
      arr.splice(index, 1);
    }

    setImages(arr);
  };

  const addImage = (img: Image) => {
    setImages([...images, img]);
  };

  const processData = (s: any) => {
    const signatureValue = s.signature !== undefined && s.signature !== false;
    const processedData = {
      ...s,
      signature: signatureValue,
      signature_data:
        signatureValue === true ? s.signature?.description : undefined,
    };

    // Remove undefined player_id and number to make them optional
    if (processedData.player_id === undefined) {
      delete processedData.player_id;
    }
    if (processedData.number === undefined) {
      delete processedData.number;
    }

    return processedData;
  };

  const sendNewShirt = () => {
    setIsLoading(true);
    let validData = checkMandatoryFields(mandatoryFields, shirtData, images);
    console.log({ validData, shirtData, images });
    if (validData === true) {
      const shirtProcessed = processData(shirtData);
      console.log({ shirtProcessed });
      newShirt(shirtProcessed)
        .then((result) => {
          if (result) {
            const shirtId = result?.id;
            if (shirtId && wornProperty) {
              let propertyCalls = [
                wornProperty
                  ? updateProperty(
                      shirtId,
                      wornProperty.id,
                      wornProperty.extra_data
                    )
                  : () => {},
              ];

              Promise.all(propertyCalls).then((result) => {
                if (shirtId && images.length > 0) {
                  updateImages(shirtId, images).then((result) => {
                    setIsLoading(false);
                    if (!result) {
                      error("Images update error");
                    } else {
                      success("Shirt created successfully");
                      closeDialog();
                    }
                  });
                } else {
                  setIsLoading(false);
                  success("Shirt created successfully");
                  closeDialog();
                }
              });
            } else {
              if (shirtId && images.length > 0) {
                updateImages(shirtId, images).then((result) => {
                  setIsLoading(false);
                  if (!result) {
                    error("Images update error");
                  } else {
                    success("Shirt created successfully");
                    closeDialog();
                  }
                });
              } else {
                setIsLoading(false);
                success("Shirt created successfully");
                closeDialog();
              }
            }
          } else {
            setIsLoading(false);
            error("Something went wrong");
          }
        })
        .catch(() => {
          setIsLoading(false);
          error("Something went wrong");
        });
    } else {
      setIsLoading(false);
      error("Please, fill mandatory fields");
    }
  };

  const validateDates = (
    date: Dayjs,
    errorKey: string,
    errorMessage: string
  ) => {
    if (date.isAfter(dayjs())) {
      error(errorMessage);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [errorKey]: true,
      }));
    } else {
      setErrors((prevErrors) => {
        const { [errorKey]: _, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  useEffect(() => {
    closeBDL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  useEffect(() => {
    const purchaseDate = shirtData.purchase_date
      ? dayjs(shirtData.purchase_date)
      : undefined;
    const wornDate = wornProperty?.extra_data?.match?.date
      ? dayjs(wornProperty?.extra_data?.match?.date)
      : undefined;

    if (purchaseDate) {
      validateDates(
        purchaseDate,
        "purchase_date",
        "Purchase date must be earlier than today."
      );
    }

    if (wornDate) {
      validateDates(
        wornDate,
        "worn_date",
        "Match date must be earlier than today."
      );
    }

    if (wornDate && purchaseDate) {
      if (purchaseDate.isBefore(wornDate)) {
        error("Match date must be earlier than purchase date.");
        setErrors((prevErrors) => ({
          ...prevErrors,
          purchase_date: true,
          worn_date: true,
        }));
      } else {
        setErrors((prevErrors) => {
          const { purchase_date, worn_date, ...rest } = prevErrors;
          return rest;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wornProperty, shirtData.purchase_date]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: 3,
      }}
    >
      <Box>
        <ShirtImages
          removeImage={removeImage}
          addImage={addImage}
          images={images}
          error={errors["images"]}
        />
      </Box>
      <Box>
        <NewShirtForm
          setShirtData={updateField}
          setProperties={setWornProperty}
          errors={errors}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box>
          <Button
            className="default-btn move-right"
            onClick={sendNewShirt}
            disabled={isLoading}
            sx={{
              width: "250px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "#fff",
              fontWeight: 600,
              fontStyle: "italic",
            }}
          >
            {isLoading && <CircularProgress size={20} color="inherit" />}
            <span>{isLoading ? "Adding..." : "Add to collection"}</span>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NewShirtView;
